import { Box, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { formatterService } from "../../NumberFormatter";

import ChartCard from "./ChartCard";
import { FEATURE_LABEL_KEYS, percent, SERIES_COLORS } from "./constants";
import { ForecastMetrics } from "./services";

/**
 * Ranked bars rather than a chart library: a single series of eleven values reads better as a
 * labelled list, and every value carries its own number so identity never rests on colour.
 */
const FeatureImportance = ({ metrics }: { metrics: ForecastMetrics }) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  const entries = Object.entries(metrics.feature_importances).sort(
    (left, right) => right[1] - left[1]
  );
  const max = entries.length > 0 ? entries[0]?.[1] ?? 0 : 0;

  if (entries.length === 0) return null;

  return (
    <ChartCard
      title={intl.formatMessage({ id: "ML_FORECAST_IMPORTANCE_TITLE" })}
      subtitle={intl.formatMessage({ id: "ML_FORECAST_IMPORTANCE_SUBTITLE" })}
      help={
        <Box sx={{ maxWidth: 300 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {intl.formatMessage({ id: "ML_FORECAST_IMPORTANCE_HELP_TITLE" })}
          </Typography>
          <Typography variant="caption">
            {intl.formatMessage({ id: "ML_FORECAST_IMPORTANCE_HELP" })}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {entries.map(([feature, importance]) => (
          <Box
            key={feature}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                width: 160,
                flexShrink: 0,
                color: "text.secondary",
                textAlign: "right",
              }}
            >
              {FEATURE_LABEL_KEYS[feature]
                ? intl.formatMessage({ id: FEATURE_LABEL_KEYS[feature] })
                : feature}
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                height: 8,
                borderRadius: 1,
                bgcolor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${max > 0 ? (importance / max) * 100 : 0}%`,
                  minWidth: importance > 0 ? 2 : 0,
                  borderRadius: 1,
                  bgcolor: SERIES_COLORS.ml,
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ width: 48, textAlign: "right" }}
            >
              {percent(formatter, importance)}
            </Typography>
          </Box>
        ))}
      </Box>
    </ChartCard>
  );
};

export default FeatureImportance;
